import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Mail } from 'lucide-react';
import Layout from './Layout'; 

const faqData = [
    {
        question: "What exactly is SubHack?",
        answer: "SubHack is the fastest way to remix video culture. You pick a clip (or 'hack' an existing one), type and place your subtitles, and get a unique link to share your creation. No complex video editing software required - just a touch of imagination!"
    },
    {
        question: "Is SubHack free to use?",
        answer: "Yes! As a free user you will have access to a limited selection of featured clips. You can create, save, and share one SubHack at a time. To create a new SubHack, you'll need to delete your old one. The Free plan includes watermarks and a 7-day lifespan for your creation. For permanent links and power features, check out our Pro plan."
    },
    {
        question: "What are the benefits of going Pro?",
        answer: "Pro is the 'no limits' version of SubHack. You get unlimited projects, permanent share links (Free links expire after 7 days), zero watermarks, and no ads when watching clips while logged in. It also unlocks your full creative toolkit: custom fonts, colors, and downloadable .SRT files."
    },
    {
        question: "What is 'Founder Pricing'?",
        answer: "Our Founder Pricing is a limited-time offer for early adopters. If you subscribe now, you lock in the current rate ($5/mo or $48/yr) for as long as your subscription remains continuously active, even when we raise prices later. Annual Founders also gain access to the Founder's Vault (containing otherwise unavailable clips) and voting rights on our product roadmap."
    },
    {
        question: "How does the 'Voting Rights' feature work?",
        answer: "We believe our best users should help build the app. Annual Pro subscribers are invited to our inner circle, where they can suggest features and vote on what we build next. You're not just a user; you're a partner."
    },
    {
        question: "Can I download my videos?",
        answer: "Currently, SubHack is designed for instant web sharing via unique URLs. However, Pro users can download the .SRT (subtitle) files to use in other editing software. Full video downloads are coming soon to the roadmap!"
    },
    {
        question: "How do I cancel my subscription?",
        answer: "You can manage or cancel your subscription at any time via the billing portal in your account settings. If you cancel, you will keep your Pro benefits until the end of your current billing period."
    }
];

const FAQItem = ({ question, answer, isOpen, onClick }) => {
    return (
        <div className="border-b border-border last:border-0">
            <button 
                className="w-full py-6 flex items-center justify-between text-left focus:outline-none group gap-4"
                onClick={onClick}
            >
                <span className={`font-heading text-lg transition-colors duration-300 ${isOpen ? 'text-primary' : 'text-foreground group-hover:text-primary'}`}>
                    {question}
                </span>
                {isOpen ? (
                    <ChevronUp className="w-5 h-5 text-primary shrink-0" />
                ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                )}
            </button>
            <div 
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isOpen ? 'max-h-96 opacity-100 mb-6' : 'max-h-0 opacity-0'
                }`}
            >
                <p className="text-muted-foreground leading-relaxed">
                    {answer}
                </p>
            </div>
        </div>
    );
};

export default function FAQ() {
    const [openIndex, setOpenIndex] = useState(null);

    const toggleFAQ = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <Layout>
            <section className="py-24 bg-card/30" id="faq">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="font-brand text-4xl md:text-5xl uppercase mb-4">
                            Frequently <span className="text-primary">Asked</span>
                        </h2>
                        <p className="text-muted-foreground">
                            Everything you need to know about the platform.
                        </p>
                    </div>

                    <div className="card-base p-8 mb-16">
                        {faqData.map((item, index) => (
                            <FAQItem 
                                key={index}
                                question={item.question}
                                answer={item.answer}
                                isOpen={openIndex === index}
                                onClick={() => toggleFAQ(index)}
                            />
                        ))}
                    </div>

                    {/* Support Block */}
                    <div className="text-center animate-slide-up">
                        <p className="text-muted-foreground mb-6">
                            Still have questions? We're actually real humans.
                        </p>
                        <a 
                            href="mailto:support@subhack.app?subject=Help with SubHack"
                            className="inline-flex items-center gap-2 btn-secondary px-6 py-3 rounded-full hover:bg-primary hover:text-black transition-all duration-300"
                        >
                            <Mail className="w-4 h-4" />
                            <span className="font-heading uppercase tracking-wider text-sm">Contact Support</span>
                        </a>
                    </div>
                </div>
            </section>
        </Layout>
    );
}